import React, { useEffect, useMemo, useRef } from 'react'
import { Product } from '@news-mono/web-common'
import type { Intention } from '@news-mono/web-common'
import styled from '@emotion/styled'
import { tokens } from '@news-mono/design-tokens'
import {
    HierarchicalIntent,
    TextItemProps,
    ValidTextElements,
} from './TextItem'
import { createIntents } from './helpers/create_intents'
import { TextLink } from '../TextLink/TextLink'
import { TtsWordInParagraph } from '../../publication/TtsText'

function getCurrentWordHighlightColors(themeKind: unknown): {
    backgroundColor: string
    color: string
} {
    switch (themeKind) {
        case Product.SevenNews:
        case 'sevennews':
            return {
                backgroundColor: tokens.sevennews.colors.palette.red,
                color: tokens.sevennews.colors.palette.white,
            }
        case Product.TheNightly:
        case 'thenightly':
            return {
                backgroundColor:
                    tokens.thenightly.colors.palette.utility.error.default,
                color: tokens.thenightly.colors.palette.white,
            }
        case Product.PerthNow:
        case 'perthnow':
            return {
                backgroundColor: tokens.perthnow.colors.palette.pinkGarnet,
                color: tokens.perthnow.colors.palette.white,
            }
        case Product.TheWest:
        case 'thewest':
            return {
                backgroundColor: tokens.thewest.colors.brand,
                color: tokens.thewest.colors.palette.white,
            }
        default:
            return {
                backgroundColor: tokens.sevennews.colors.palette.red,
                color: tokens.sevennews.colors.palette.white,
            }
    }
}

const CurrentWordHighlight = styled('span')(({ theme }) => {
    const { backgroundColor, color } = getCurrentWordHighlightColors(theme?.kind)
    return { backgroundColor, color, borderRadius: '2px' }
})

/**
 * Cache word-boundary regexes by needle to avoid recreating the same pattern on repeated word searches.
 * Capped with simple LRU (oldest entry evicted when full) to avoid unbounded growth on long sessions.
 */
const WORD_BOUNDARY_CACHE_MAX_SIZE = 200
const wordBoundaryRegexCache = new Map<string, RegExp>()

function getWordBoundaryRegex(rawNeedle: string): RegExp | null {
    if (!rawNeedle || rawNeedle.length === 0) return null
    let re = wordBoundaryRegexCache.get(rawNeedle)
    if (re) {
        // Move to end (most recently used) so it isn't evicted soon
        wordBoundaryRegexCache.delete(rawNeedle)
        wordBoundaryRegexCache.set(rawNeedle, re)
        return re
    }
    if (wordBoundaryRegexCache.size >= WORD_BOUNDARY_CACHE_MAX_SIZE) {
        const oldestKey = wordBoundaryRegexCache.keys().next().value
        if (oldestKey !== undefined) {
            wordBoundaryRegexCache.delete(oldestKey)
        }
    }
    const escaped = rawNeedle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    re = new RegExp(`\\b${escaped}\\b`, 'g')
    wordBoundaryRegexCache.set(rawNeedle, re)
    return re
}

export interface TtsAwareTextItemProps extends TextItemProps {
    ttsWords?: TtsWordInParagraph[]
    briefPrefixLength?: number
    ttsOffsetShift?: number
}

export function TtsAwareTextItem(
    props: TtsAwareTextItemProps & {
        currentWordIndex?: number
        isPlaying?: boolean
    },
) {
    const {
        text,
        intentions,
        onEvent,
        openLinksInNewWindow,
        isNoFollow,
        tag,
        idAttribute,
        className,
        currentWordIndex,
        ttsWords,
        briefPrefixLength,
        ttsOffsetShift,
        isPlaying,
    } = props

    // Calculate highlight ranges using preprocessed data
    // This is much simpler than the old algorithm - no boundary checks, no fuzzy matching needed!
    const highlightRanges = useMemo(() => {
        if (
            typeof currentWordIndex !== 'number' ||
            !Array.isArray(ttsWords) ||
            ttsWords.length === 0
        ) {
            return []
        }

        const prefixLen = briefPrefixLength || 0
        const shift = ttsOffsetShift || 0

        // Find the current word in this paragraph's preprocessed words
        const activeWordIndex = ttsWords.findIndex(
            (w) => w.globalWordIndex === currentWordIndex,
        )
        const activeWord =
            activeWordIndex >= 0 ? ttsWords[activeWordIndex] : undefined
        if (!activeWord) {
            return []
        }

        const paragraphText = text.slice(prefixLen)
        const needle = activeWord.value
        // localStart is in trimmed paragraph space (TTS body is built from trimmed text).
        // paragraphText is untrimmed; use shift only for fallback expected position.
        const expected = Math.max(
            0,
            Math.min(paragraphText.length, activeWord.localStart + shift),
        )

        // Which occurrence of this word value in the paragraph (0-based). Handles duplicates like "role", "best", "I've".
        const sameValueInParagraph = ttsWords.filter(
            (w) => w.value === activeWord.value,
        )
        const occurrenceIndex = sameValueInParagraph.findIndex(
            (w) => w.globalWordIndex === activeWord.globalWordIndex,
        )
        const occurrenceRank = occurrenceIndex >= 0 ? occurrenceIndex : 0

        /** Find the (occurrenceRank+1)th occurrence of rawNeedle in haystack (word-boundary). */
        const findNthOccurrence = (
            haystack: string,
            rawNeedle: string,
            n: number,
        ): number | null => {
            if (!rawNeedle || rawNeedle.length === 0 || n < 0) return null
            const wordBoundaryRegex = getWordBoundaryRegex(rawNeedle)
            if (!wordBoundaryRegex) return null
            wordBoundaryRegex.lastIndex = 0
            let count = 0
            let match
            while ((match = wordBoundaryRegex.exec(haystack)) !== null) {
                if (count === n) return match.index
                count += 1
            }
            return null
        }

        const findNearByPosition = (
            haystack: string,
            rawNeedle: string,
            expectedPos: number,
            windowSize = 48,
        ): number | null => {
            if (!rawNeedle || rawNeedle.length === 0) return null
            const start = Math.max(0, expectedPos - windowSize)
            const end = Math.min(
                haystack.length,
                expectedPos + windowSize + rawNeedle.length,
            )
            const window = haystack.slice(start, end)
            let bestAbs: number | null = null
            let bestDist = Number.POSITIVE_INFINITY
            const wordBoundaryRegex = getWordBoundaryRegex(rawNeedle)
            if (wordBoundaryRegex) {
                wordBoundaryRegex.lastIndex = 0
                let match
                while ((match = wordBoundaryRegex.exec(window)) !== null) {
                    const absIdx = start + match.index
                    if (absIdx <= expectedPos) {
                        const dist = Math.abs(absIdx - expectedPos)
                        if (dist < bestDist) {
                            bestDist = dist
                            bestAbs = absIdx
                        }
                    }
                }
            }
            if (bestAbs === null) {
                let from = 0
                while (from <= window.length - rawNeedle.length) {
                    const localIdx = window.indexOf(rawNeedle, from)
                    if (localIdx === -1) break
                    const absIdx = start + localIdx
                    if (
                        absIdx <= expectedPos &&
                        Math.abs(absIdx - expectedPos) < bestDist
                    ) {
                        bestDist = Math.abs(absIdx - expectedPos)
                        bestAbs = absIdx
                    }
                    from = localIdx + 1
                }
            }
            return (
                bestAbs ?? (expectedPos < haystack.length ? expectedPos : null)
            )
        }

        // Prefer Nth-occurrence match so duplicate words (e.g. "role", "best") highlight correctly
        let matchStart: number | null = findNthOccurrence(
            paragraphText,
            needle,
            occurrenceRank,
        )
        if (matchStart === null) {
            matchStart = findNearByPosition(paragraphText, needle, expected)
        }
        const matchLen = needle.length

        // matchStart/expected are in untrimmed paragraphText; adding shift would double-count
        // leading whitespace. Only add prefixLen to get absolute position in text.
        const start = (matchStart !== null ? matchStart : expected) + prefixLen
        const end = start + matchLen

        // Final safety check: ensure the highlighted text matches the word value
        const highlightedText = text.slice(start, end)
        const normalizedHighlighted = highlightedText
            .replace(/\s+/g, '')
            .toLowerCase()
        const normalizedNeedle = needle.replace(/\s+/g, '').toLowerCase()

        // Also check for hyphenated words
        const highlightedWithHyphen = highlightedText
            .replace(/\s*-\s*/g, '-')
            .toLowerCase()
        const needleWithHyphen = needle.replace(/\s*-\s*/g, '-').toLowerCase()

        const matches =
            highlightedText === needle ||
            normalizedHighlighted === normalizedNeedle ||
            highlightedWithHyphen === needleWithHyphen

        if (!matches || end <= start) {
            // If the highlighted text doesn't match, don't highlight to avoid partial matches
            return []
        }

        return [{ start, end }]
    }, [currentWordIndex, ttsWords, briefPrefixLength, ttsOffsetShift, text])

    const highlightRange =
        highlightRanges.length > 0 ? highlightRanges[0] : null

    const markRef = useRef<HTMLSpanElement>(null)
    const didBindRef = useRef(false)
    const offsetRef = useRef(0)
    const lastScrolledParagraphRef = useRef<HTMLElement | null>(null)

    useEffect(() => {
        // Reset binding on word change so we attach ref to the new highlighted segment.
        didBindRef.current = false
    }, [currentWordIndex])

    useEffect(() => {
        // Reset scroll tracking when playback stops
        if (!isPlaying) {
            lastScrolledParagraphRef.current = null
            return
        }

        if (highlightRange && markRef.current) {
            // Find the paragraph element (<p> tag) containing the highlighted word
            const paragraphElement = markRef.current.closest('p')

            // Only scroll when the paragraph changes, not on every word update
            // This prevents janky scrolling during rapid word changes (multiple times per second)
            if (
                paragraphElement &&
                paragraphElement !== lastScrolledParagraphRef.current
            ) {
                lastScrolledParagraphRef.current = paragraphElement
                // Scroll to center of the paragraph for better reading experience
                paragraphElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'nearest',
                })
            } else if (
                !paragraphElement &&
                markRef.current !== lastScrolledParagraphRef.current
            ) {
                // Fallback to scrolling to the word itself if paragraph not found
                // Only scroll if we haven't already scrolled to this element
                lastScrolledParagraphRef.current = markRef.current
                markRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'nearest',
                })
            }
        }
    }, [isPlaying, highlightRange, currentWordIndex])

    const intents = useMemo<HierarchicalIntent[]>(() => {
        return createIntents((intentions || []) as Intention[], text)
    }, [intentions, text])

    const renderIntentContent = (nestedIntents: HierarchicalIntent[]) => {
        if (!nestedIntents || nestedIntents.length === 0) return null
        const rendered = nestedIntents.map((intent, idx) =>
            renderIntents(intent, idx),
        )
        return rendered.length === 1 ? rendered[0] : rendered
    }

    const highlightTextSegment = (
        value: string,
        absStart: number | null,
        keyPrefix: string,
    ) => {
        if (absStart === null || !highlightRange) return value

        const start = highlightRange.start
        const end = highlightRange.end

        // Check for overlap with the current text segment (value)
        const overlapStart = Math.max(absStart, start)
        const overlapEnd = Math.min(absStart + value.length, end)

        if (overlapEnd <= overlapStart) {
            // No overlap
            return value
        }

        // Add text before the highlight
        const before = value.slice(0, overlapStart - absStart)

        // Add the highlighted part
        const shouldAttachRef = !didBindRef.current
        if (shouldAttachRef) didBindRef.current = true

        const highlightedPart = (
            <mark key={`${keyPrefix}-mark`}>
                <CurrentWordHighlight
                    ref={shouldAttachRef ? markRef : undefined}
                    data-tts-current-word
                >
                    {value.slice(
                        overlapStart - absStart,
                        overlapEnd - absStart,
                    )}
                </CurrentWordHighlight>
            </mark>
        )

        // Add any remaining text after the highlight
        const after = value.slice(overlapEnd - absStart)

        return (
            <>
                {before}
                {highlightedPart}
                {after}
            </>
        )
    }

    // Reset per render pass.
    // We track a cursor (offsetRef) in the original `text` and try to align each rendered
    // leaf segment against it.
    offsetRef.current = 0

    const findSegmentStartNearCursor = (
        segment: string,
        cursor: number,
        windowSize = 256,
    ): number | null => {
        if (!segment) return null

        // Fast path: exact alignment at cursor.
        if (text.slice(cursor, cursor + segment.length) === segment) {
            return cursor
        }

        // Window search (backwards then forwards) to resync.
        const start = Math.max(0, cursor - windowSize)
        const end = Math.min(text.length, cursor + windowSize)
        const haystack = text.substring(start, end)
        if (segment.length === 0) return null

        // Find ALL occurrences and pick the one closest to the cursor. This prevents drift
        // when the same segment appears multiple times inside the window.
        let bestAbs: number | null = null
        let bestDist = Number.POSITIVE_INFINITY
        let from = 0
        while (from <= haystack.length - segment.length) {
            const localIdx = haystack.indexOf(segment, from)
            if (localIdx === -1) break
            const absIdx = start + localIdx
            const dist = Math.abs(absIdx - cursor)
            if (dist < bestDist) {
                bestDist = dist
                bestAbs = absIdx
            }
            from = localIdx + 1
        }

        return bestAbs
    }

    const renderIntents = (intent: HierarchicalIntent, idx: number): any => {
        switch (intent.kind) {
            case 'emphasized':
                return (
                    <em key={idx}>
                        {renderIntentContent(intent.nestedIntents)}
                    </em>
                )
            case 'important':
                return (
                    <strong key={idx}>
                        {renderIntentContent(intent.nestedIntents)}
                    </strong>
                )
            case 'link':
                return (
                    <TextLink
                        key={idx}
                        href={intent.href}
                        dataLinkType="article-inline"
                        openInNewWindow={openLinksInNewWindow}
                        isNoFollow={isNoFollow}
                        onEvent={onEvent}
                    >
                        {renderIntentContent(intent.nestedIntents)}
                    </TextLink>
                )
            case 'line-break':
                return <br key={idx} />
            case 'text':
            default:
                // Align this rendered segment against the original `text` cursor.
                {
                    const candidate = offsetRef.current
                    const absStart = findSegmentStartNearCursor(
                        intent.value,
                        candidate,
                    )
                    if (absStart !== null) {
                        offsetRef.current = absStart + intent.value.length
                        return highlightTextSegment(
                            intent.value,
                            absStart,
                            `tts-${idx}`,
                        )
                    }
                }

                // If we can't align this segment, don't advance the cursor.
                return intent.value
        }
    }

    const CustomTag: ValidTextElements = tag || 'p'
    const rendered = intents.map((intent, i) => renderIntents(intent, i))

    return (
        <CustomTag className={className} id={idAttribute}>
            {rendered.length === 1 ? rendered[0] : rendered}
        </CustomTag>
    )
}
